import type {
  PermissionMapping,
  Policy,
  PolicyList,
} from "@eai-toolkit/client";

import type { ResourceFieldValue } from "../../../../data/types";
import { toResourceType } from "../../../../store/api/resourceTypes";

export type RuleSourceKind = "direct" | "role" | "team" | "unknown";

export const SOURCE_KIND_BACKEND_VALUES: Record<
  Exclude<RuleSourceKind, "unknown">,
  string[]
> = {
  team: ["team", "role:team"],
  role: ["role:user"],
  direct: ["direct"],
};

// Of the two team sources, only "role:team" includes an extra role hop.
export const BACKEND_SOURCE_VIA_TEAM_ROLE = "role:team";

export type PermissionsSource = Exclude<RuleSourceKind, "unknown"> | "";

export function sourceToBackendValues(source: PermissionsSource): string[] {
  return source === "" ? [] : SOURCE_KIND_BACKEND_VALUES[source];
}

export function backendValuesToSource(
  values: (string | null)[],
): PermissionsSource {
  for (const value of values) {
    if (value == null) continue;
    const kind = parseSourceKind(value);
    if (kind !== "unknown") return kind;
  }
  return "";
}

function parseSourceKind(source: string | undefined): RuleSourceKind {
  if (source === undefined) return "unknown";
  for (const kind of Object.keys(SOURCE_KIND_BACKEND_VALUES) as Array<
    Exclude<RuleSourceKind, "unknown">
  >) {
    if (SOURCE_KIND_BACKEND_VALUES[kind].includes(source)) return kind;
  }
  return "unknown";
}

export type RuleSource = {
  kind: RuleSourceKind;
  id: string | null; // parent full name under human=true (team/role for inherited, user for direct); null if absent
  // True for backend source "role:team" (user → team → role → policy). Both
  // "team" and "role:team" map to kind "team", but this flag distinguishes the
  // extra role hop.
  viaTeamRole?: boolean;
};

export type RuleList = {
  items: Rule[];
  // `extra.hidden` is set when a policy listing withholds rows the caller may
  // not see; a completed response without it means no rows were withheld.
  metadata: PolicyList["metadata"];
};

export function getHiddenPoliciesCount(
  response: RuleList | undefined,
): number | undefined {
  if (!response) return undefined;
  const hidden = response.metadata.extra?.hidden;
  return typeof hidden === "number" ? hidden : 0;
}

export type Rule = {
  id: string; // Synthetic unique key for rule: "{policyId}-{created}-{source}-{target}"
  policyId: string; // Policy UUID
  action: string;
  expression: string;
  resource: ResourceFieldValue;
  source: RuleSource;
  expires: string | null; // ISO string
  cascades: boolean;
  shareable: boolean;
  scope: string;
  created: string | null; // ISO string
  creator: string | null;
};

type ValidPolicy = Policy & { action: string; expression: string };

export function isValidPolicy(policy: Policy): policy is ValidPolicy {
  return policy.action != null && policy.expression != null;
}

export type ParsedResource = { type: string; id: string };

/**
 * Parses the resource from a policy `expression` URN:
 *   <orgId>[:account:<acctId>][:<type>:<resourceId>][:*]  (may be `locked:`-prefixed)
 * The resource is the *deepest* `type:id` pair — not the `action`, which only
 * names the operation (e.g. `job:access` on a whole account). Null for
 * org-level/global grants, which target no specific resource.
 */
export function parseResourceFromExpression(
  expression: string,
): ParsedResource | null {
  const segments = expression
    .replace(/^locked:/, "")
    .split(":")
    .filter((s) => s && s !== "*");
  // segments[0] is the org UUID; the rest are type:id pairs.
  let resource: ParsedResource | null = null;
  for (let i = 1; i + 1 < segments.length; i += 2) {
    resource = { type: segments[i], id: segments[i + 1] };
  }
  return resource;
}

/**
 * Given a resource URN like "org:account:acctId:type:resourceId", returns every
 * policy `expression` that grants access to it.
 */
export function resourceUrnToExpressionAncestry(urn: string): string[] {
  const segments = urn.split(":");
  if (segments.length < 1 || segments[0] === "") return [];
  const expressions = ["*"]; // global grant
  // Each strict ancestor's cascading wildcard ("org:*", "org:account:acctId:*").
  for (let end = 1; end < segments.length; end += 2) {
    expressions.push(`${segments.slice(0, end).join(":")}:*`);
  }
  // The resource itself (a non-cascading grant directly on it).
  expressions.push(urn);
  return expressions;
}

export type PermissionsAccount = {
  id: string;
  name: string;
  // Whether the listed user can manage policies across the whole account;
  // drives the admin badge in the account filter.
  isAdmin: boolean;
};

// The action the backend requires to write policies — the same predicate that
// gates row-level deletes. `*` grants (e.g. the org admin role) match it too.
const ACCOUNT_ADMIN_ACTION = "policy:set";

// Scopes conferring account-wide policy management. Single-resource grants are
// excluded: managing one dataset's policies doesn't admin its whole account.
type AdminScopes = {
  // `*` — every account in every visible org.
  global: boolean;
  // `<org>:*` grants — every account under them.
  orgs: Set<string>;
  // "<org>:<accountId>" keys from `<org>:account:<id>:*` grants.
  accounts: Set<string>;
};

function deriveAdminScopes(rules: Rule[]): AdminScopes {
  const scopes: AdminScopes = {
    global: false,
    orgs: new Set<string>(),
    accounts: new Set<string>(),
  };
  for (const rule of rules) {
    if (!permissionPatternMatches(rule.action, ACCOUNT_ADMIN_ACTION)) continue;
    const segments = rule.expression.replace(/^locked:/, "").split(":");
    // Only cascading grants can cover a whole account.
    if (segments[segments.length - 1] !== "*") continue;
    if (segments.length === 1) {
      scopes.global = true;
    } else if (segments.length === 2) {
      scopes.orgs.add(segments[0]);
    } else if (segments.length === 4 && segments[1] === "account") {
      scopes.accounts.add(`${segments[0]}:${segments[2]}`);
    }
  }
  return scopes;
}

// An account read off a single rule, before admin-ness is resolved.
type RuleAccount = Pick<PermissionsAccount, "id" | "name">;

export function extractRuleAccount(rule: Rule): RuleAccount | null {
  const exprParts = rule.expression.split(":");
  const accountIndex = exprParts.indexOf("account");
  const id = exprParts[accountIndex + 1];
  if (accountIndex === -1 || !id) return null;

  const nameParts = (rule.resource.name ?? "").split(".");
  // fullName is "org.account.resource…"; fall back to the id when unavailable.
  const name = nameParts.length >= 2 ? nameParts[1] : id;
  return { id, name };
}

export function deriveUniqueAccounts(rules: Rule[]): PermissionsAccount[] {
  const adminScopes = deriveAdminScopes(rules);
  const byId = new Map<string, PermissionsAccount>();
  for (const rule of rules) {
    const account = extractRuleAccount(rule);
    if (!account || byId.has(account.id)) continue;
    const org = rule.expression.replace(/^locked:/, "").split(":")[0];
    byId.set(account.id, {
      ...account,
      isAdmin:
        adminScopes.global ||
        adminScopes.orgs.has(org) ||
        adminScopes.accounts.has(`${org}:${account.id}`),
    });
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

// Actions have no privilege hierarchy — each grants an orthogonal set of
// permissions (write-data → data:write, update-data → data:set). So the ceiling
// for a resource type is the union across all its actions: hold every one and
// nothing's left to request. Mirrors the backend allPermissions table.
export function getRequestablePermissions(
  mappings: PermissionMapping[],
  resourceType: string,
): Set<string> {
  const permissions = new Set<string>();
  for (const mapping of mappings) {
    if (mapping.resourceType !== resourceType) continue;
    for (const permission of mapping.permissions ?? []) {
      permissions.add(permission);
    }
  }
  return permissions;
}

// A held `action` is a permission *pattern* where "*" matches any run of chars,
// mirroring the backend's `required LIKE action` check ("%" → "*" on the wire).
// So "*" grants everything, "job:*" every job permission, "*:get" every get.
// Implemented as a linear, two-pointer wildcard match (no RegExp) so the
// dynamically-built `pattern` can't introduce a ReDoS; the match is anchored
// at both ends.
function permissionPatternMatches(pattern: string, value: string): boolean {
  const segments = pattern.split("*");
  // No wildcard: the pattern must equal the value exactly.
  if (segments.length === 1) return pattern === value;

  const first = segments[0];
  const last = segments[segments.length - 1];
  if (!value.startsWith(first) || !value.endsWith(last)) return false;

  // Each interior segment must appear in order, after the prefix.
  let idx = first.length;
  for (let i = 1; i < segments.length - 1; i++) {
    const seg = segments[i];
    if (seg === "") continue;
    const found = value.indexOf(seg, idx);
    if (found === -1) return false;
    idx = found + seg.length;
  }
  // Interior matches must not overrun into the suffix region.
  return idx <= value.length - last.length;
}

// Requestable permissions for `resourceType` not already covered by the user's
// held `action` patterns. Empty = nothing left worth requesting.
export function getMissingRequestablePermissions(
  mappings: PermissionMapping[],
  resourceType: string | undefined,
  heldPermissions: string[],
): string[] {
  if (!resourceType || mappings.length === 0) return [];

  const requestable = getRequestablePermissions(mappings, resourceType);
  if (requestable.size === 0) return [];

  return [...requestable].filter(
    (p) =>
      !heldPermissions.some((pattern) => permissionPatternMatches(pattern, p)),
  );
}

// Whether held grants already cover everything requestable for `resourceType`,
// making a request pointless. False when type is unknown, the catalog is empty,
// or nothing's requestable — so callers default to showing the affordance.
export function userHasAllRequestableAccess(
  mappings: PermissionMapping[],
  resourceType: string | undefined,
  heldPermissions: string[],
): boolean {
  if (!resourceType || mappings.length === 0) return false;
  if (getRequestablePermissions(mappings, resourceType).size === 0) {
    return false;
  }
  return (
    getMissingRequestablePermissions(mappings, resourceType, heldPermissions)
      .length === 0
  );
}

// The "type:id" key for the resource a grant targets, or null for grants that
// target no specific resource (org/global level). Used to group rows by resource.
export function resourceKeyFromExpression(expression: string): string | null {
  const resource = parseResourceFromExpression(expression);
  return resource ? `${resource.type}:${resource.id}` : null;
}

// Resource detail pages require the resource's *get* permission (e.g. data:get),
// which is orthogonal to read/write/rm — holding only data:read doesn't let you
// open the page (it 403s). So a "Go to resource" link must be gated on get, not
// merely on having any grant. Aggregates each resource's held action patterns
// across the given rules and returns the keys ("type:id") the user can open.
export function deriveViewableResourceKeys(rules: Rule[]): Set<string> {
  const actionsByResource = new Map<string, string[]>();
  for (const rule of rules) {
    const key = resourceKeyFromExpression(rule.expression);
    if (!key) continue;
    const actions = actionsByResource.get(key) ?? [];
    actions.push(rule.action);
    actionsByResource.set(key, actions);
  }

  const viewable = new Set<string>();
  for (const [key, actions] of actionsByResource) {
    const type = key.slice(0, key.indexOf(":"));
    const getPermission = `${type}:get`;
    if (
      actions.some((action) => permissionPatternMatches(action, getPermission))
    ) {
      viewable.add(key);
    }
  }
  return viewable;
}

export function mapPolicyToRule(policy: ValidPolicy): Rule {
  const cascades = policy.cascades ?? false;
  const parsedResource = parseResourceFromExpression(policy.expression);
  return {
    id: `${policy.id}-${policy.created}-${policy.source ?? ""}-${policy.target ?? ""}`,
    policyId: policy.id,
    action: policy.action,
    expression: policy.expression,
    resource: {
      name: policy.resource ?? policy.expression,
      resourceType: toResourceType(parsedResource?.type),
      cascades,
    },
    source: {
      // The backend always populates `target` for the policies this endpoint
      // returns (direct grants resolve to the user's own full name under
      // human=true; inherited grants to the team/role full name). The `?? null`
      // only satisfies the SDK's optional `target?: string` typing — a null id
      // is not an expected runtime state.
      kind: parseSourceKind(policy.source),
      id: policy.target ?? null,
      viaTeamRole: policy.source === BACKEND_SOURCE_VIA_TEAM_ROLE,
    },
    // Treat empty strings as "no date" (null), not as a present-but-blank value.
    expires: policy.expiration || null,
    cascades,
    shareable: policy.shareable ?? false,
    scope: policy.scope ?? "",
    created: policy.created || null,
    creator: policy.creator || null,
  };
}
